import { DatePickerFr } from '../ui/DatePickerFr';
import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { CheckCircle, Building2, Mail, User, Loader2, Trash2, Repeat, Edit, Save, AlertCircle, CalendarClock, CalendarDays} from 'lucide-react';
export default function ModalsGroupB({
  // Validation Modal
  showValidationModal, setShowValidationModal, selectedDocument,
  validationData, setValidationData, validating, handleValidatePayment,
  calculateMontantTTC, calculateMontantHT,
  // Assign Modal
  showAssignModal, setShowAssignModal, assignForm, setAssignForm,
  handleAssign, coopers, members, experts,
  // Share Modal
  showShareModal, setShowShareModal, shareForm, setShareForm,
  sharing, handleShare,
  // Partial Payment Modal
  showPartialPaymentModal, setShowPartialPaymentModal,
  partialPaymentData, setPartialPaymentData,
  partialPaymentsHistory, loadingPartialPayments,
  processingPartialPayment, handlePartialPayment,
  downloadPartialReceipt, archiveRecuToGED: archiveRecuToGED,
  openRecuEcheanceModal: openRecuEcheanceModal, generateRecuEcheance: generateRecuEcheance, generatingRecu: generatingRecu,
  // Echeance Modal
  showEcheanceModal, setShowEcheanceModal,
  echeanceForm, setEcheanceForm, updatingEcheance, handleUpdateEcheance,
  // Echeancier Modal
  showEcheancierModal, setShowEcheancierModal,
  echeancierForm, setEcheancierForm, creatingEcheancier,
  handleCreateEcheancier, addEcheanceToForm, removeEcheanceFromForm,
  // Stripe SEPA Modal
  showStripeSepaModal, setShowStripeSepaModal,
  stripeSepaType, setStripeSepaType, stripeProcessing,
  sepaModalEmail, setSepaModalEmail, sepaModalName, setSepaModalName,
  setupStripeSEPAWithEmail,
  // Edit Acompte Modal
  showEditAcompteModal, setShowEditAcompteModal,
  acompteForm, setAcompteForm, saveAcompteModification,
  // Auth (for embedded PaymentLinkSection inside Partial Payment Modal)
  getAuthHeader = () => ({}),
  // Stripe actions (V37.07 — used by CB / SEPA buttons in Partial Payment Modal)
  initiateStripeCBPayment = () => {},
  openStripeSepaModal = () => {}
}) {
  return (
    <>
      <Dialog open={showEcheanceModal} onOpenChange={setShowEcheanceModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-blue-600" />
              Modifier la prochaine échéance
            </DialogTitle>
            <DialogDescription>
              {selectedDocument?.raison_sociale} - {selectedDocument?.numero_police}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Date de prochaine échéance</Label>
              <DatePickerFr
                value={echeanceForm.date_prochaine_echeance}
                onChange={(v) => setEcheanceForm({...echeanceForm, date_prochaine_echeance: v})}
                className="mt-1 w-full"
              />
            </div>
            
            <div>
              <Label>Observations (optionnel)</Label>
              <Textarea
                value={echeanceForm.observations}
                onChange={(e) => setEcheanceForm({...echeanceForm, observations: e.target.value})}
                placeholder="Notes sur cette échéance..."
                className="mt-1"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEcheanceModal(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleUpdateEcheance}
              disabled={updatingEcheance || !echeanceForm.date_prochaine_echeance}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {updatingEcheance ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Mise à jour...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Enregistrer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de création d'échéancier */}
      <Dialog open={showEcheancierModal} onOpenChange={setShowEcheancierModal}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-purple-600" />
              Créer un échéancier de paiement
            </DialogTitle>
            <DialogDescription>
              {selectedDocument?.raison_sociale} - {selectedDocument?.numero_police}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Type d'échéancier */}
            <div>
              <Label>Type d'échéancier</Label>
              <Select 
                value={echeancierForm.type_echeancier} 
                onValueChange={(value) => setEcheancierForm({...echeancierForm, type_echeancier: value})}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manuel">Manuel</SelectItem>
                  <SelectItem value="mensuel">Mensuel</SelectItem>
                  <SelectItem value="trimestriel">Trimestriel</SelectItem>
                  <SelectItem value="semestriel">Semestriel</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Liste des échéances */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Échéances programmées</Label>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={addEcheanceToForm}
                >
                  + Ajouter une échéance
                </Button>
              </div>
              
              {echeancierForm.echeances.map((echeance, idx) => (
                <div key={idx} className="flex gap-3 items-start p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1 grid grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">Date</Label>
                      <DatePickerFr
                        value={echeance.date}
                        onChange={(v) => {
                          const newEcheances = [...echeancierForm.echeances];
                          newEcheances[idx].date = v;
                          setEcheancierForm({...echeancierForm, echeances: newEcheances});
                        }}
                        className="mt-1 w-full"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Montant (€)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={echeance.montant}
                        onChange={(e) => {
                          const newEcheances = [...echeancierForm.echeances];
                          newEcheances[idx].montant = e.target.value;
                          setEcheancierForm({...echeancierForm, echeances: newEcheances});
                        }}
                        placeholder="0.00"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Notes</Label>
                      <Input
                        value={echeance.observations}
                        onChange={(e) => {
                          const newEcheances = [...echeancierForm.echeances];
                          newEcheances[idx].observations = e.target.value;
                          setEcheancierForm({...echeancierForm, echeances: newEcheances});
                        }}
                        placeholder="Optionnel"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  {echeancierForm.echeances.length > 1 && (
                    <Button 
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 mt-5"
                      onClick={() => removeEcheanceFromForm(idx)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              
              {/* Total */}
              <div className="flex justify-end p-3 bg-purple-50 rounded-lg">
                <div className="text-right">
                  <p className="text-sm text-gray-500">Total échéancier</p>
                  <p className="text-xl font-bold text-purple-700">
                    {echeancierForm.echeances
                      .reduce((sum, e) => sum + (parseFloat(e.montant) || 0), 0)
                      .toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                  </p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEcheancierModal(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleCreateEcheancier}
              disabled={creatingEcheancier || echeancierForm.echeances.filter(e => e.date && e.montant).length === 0}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {creatingEcheancier ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Création...
                </>
              ) : (
                <>
                  <CalendarDays className="h-4 w-4 mr-2" />
                  Créer l'échéancier
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Stripe SEPA */}
      <Dialog open={showStripeSepaModal} onOpenChange={setShowStripeSepaModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Repeat className="h-5 w-5 text-blue-600" />
              Configuration Prélèvement SEPA {stripeSepaType === 'b2b' ? 'B2B' : 'Core'}
            </DialogTitle>
            <DialogDescription>
              {stripeSepaType === 'core' 
                ? 'SEPA Core - Pour les particuliers (notification J-14, remboursement possible 8 semaines)'
                : 'SEPA B2B - Pour les entreprises (notification J-1, pas de remboursement automatique)'
              }
            </DialogDescription>
          </DialogHeader>

          {selectedDocument && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-medium">{selectedDocument.raison_sociale}</span>
                  <Badge>{selectedDocument.numero_police}</Badge>
                </div>
                <div className="text-2xl font-bold text-center text-blue-700">
                  {calculateMontantTTC(selectedDocument).toLocaleString('fr-FR')} €
                </div>
              </div>

              {/* Champs éditables : Email, Nom, IBAN */}
              <div className="p-4 bg-white rounded-lg border border-gray-200 space-y-3">
                <h4 className="font-semibold text-gray-800 text-sm flex items-center gap-2">
                  <Mail className="h-4 w-4 text-blue-600" />
                  Informations du client (modifiable)
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-gray-600">Email du client *</Label>
                    <Input
                      data-testid="sepa-modal-customer-email"
                      type="email"
                      value={sepaModalEmail}
                      onChange={(e) => setSepaModalEmail(e.target.value)}
                      placeholder="email@exemple.com"
                      className="mt-1 text-sm"
                    />
                    {!sepaModalEmail && (
                      <p className="text-xs text-red-500 mt-1">Email requis pour le prélèvement SEPA</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600">Nom du titulaire *</Label>
                    <Input
                      data-testid="sepa-modal-customer-name"
                      value={sepaModalName}
                      onChange={(e) => setSepaModalName(e.target.value)}
                      placeholder="Nom complet ou raison sociale"
                      className="mt-1 text-sm"
                    />
                  </div>
                </div>
                {sepaModalEmail && sepaModalEmail !== (selectedDocument.email || '') && (
                  <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                    <AlertCircle className="h-3 w-3 flex-shrink-0" />
                    <span>L'email sera aussi enregistré sur la souscription</span>
                  </div>
                )}
              </div>

              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-800 mb-2">Caractéristiques {stripeSepaType === 'b2b' ? 'SEPA B2B' : 'SEPA Core'}</h4>
                <ul className="text-sm space-y-1 text-blue-700">
                  {stripeSepaType === 'core' ? (
                    <>
                      <li>• Notification au débiteur : 14 jours avant</li>
                      <li>• Droit au remboursement : 8 semaines sans motif</li>
                      <li>• Idéal pour : Particuliers, petites entreprises</li>
                    </>
                  ) : (
                    <>
                      <li>• Notification au débiteur : 1 jour avant</li>
                      <li>• Pas de remboursement automatique</li>
                      <li>• Idéal pour : Entreprises, relations B2B</li>
                    </>
                  )}
                </ul>
              </div>

              <div className="flex gap-2">
                <Button
                  variant={stripeSepaType === 'core' ? 'default' : 'outline'}
                  className={stripeSepaType === 'core' ? 'flex-1 bg-blue-600' : 'flex-1'}
                  onClick={() => setStripeSepaType('core')}
                >
                  <User className="h-4 w-4 mr-2" />
                  SEPA Core
                </Button>
                <Button
                  variant={stripeSepaType === 'b2b' ? 'default' : 'outline'}
                  className={stripeSepaType === 'b2b' ? 'flex-1 bg-indigo-600' : 'flex-1'}
                  onClick={() => setStripeSepaType('b2b')}
                >
                  <Building2 className="h-4 w-4 mr-2" />
                  SEPA B2B
                </Button>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStripeSepaModal(false)}>
              Annuler
            </Button>
            <Button 
              onClick={() => setupStripeSEPAWithEmail(selectedDocument, stripeSepaType)}
              disabled={stripeProcessing || !sepaModalEmail || !sepaModalName}
              className={stripeSepaType === 'b2b' ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-blue-600 hover:bg-blue-700'}
            >
              {stripeProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Configuration...
                </>
              ) : (
                <>
                  <Repeat className="h-4 w-4 mr-2" />
                  Configurer SEPA {stripeSepaType.toUpperCase()}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Modification Acompte */}
      <Dialog open={showEditAcompteModal} onOpenChange={setShowEditAcompteModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5 text-amber-600" />
              Modifier l'acompte
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="acompte-montant">Montant (€)</Label>
                <Input
                  id="acompte-montant"
                  type="number"
                  step="0.01"
                  value={acompteForm.montant}
                  onChange={(e) => setAcompteForm({...acompteForm, montant: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="acompte-date">Date</Label>
                <DatePickerFr
                  id="acompte-date"
                  value={acompteForm.date}
                  onChange={(v) => setAcompteForm({...acompteForm, date: v})}
                  className="w-full"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="acompte-mode">Mode de paiement</Label>
              <Select value={acompteForm.mode} onValueChange={(v) => setAcompteForm({...acompteForm, mode: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="virement">Virement</SelectItem>
                  <SelectItem value="sepa_core">SEPA Core</SelectItem>
                  <SelectItem value="sepa_b2b">SEPA B2B</SelectItem>
                  <SelectItem value="cb">CB Stripe</SelectItem>
                  <SelectItem value="cheque">Chèque</SelectItem>
                  <SelectItem value="especes">Espèces</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="acompte-reference">Référence paiement</Label>
              <Input
                id="acompte-reference"
                value={acompteForm.reference}
                onChange={(e) => setAcompteForm({...acompteForm, reference: e.target.value})}
                placeholder="Ex: VIR-2024-001"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="acompte-scheduled"
                checked={acompteForm.isScheduled}
                onChange={(e) => setAcompteForm({...acompteForm, isScheduled: e.target.checked})}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="acompte-scheduled">Paiement programmé</Label>
            </div>

            {acompteForm.isScheduled && (
              <div>
                <Label htmlFor="acompte-scheduled-date">Date prélèvement programmé</Label>
                <DatePickerFr
                  id="acompte-scheduled-date"
                  value={acompteForm.scheduledDate}
                  onChange={(v) => setAcompteForm({...acompteForm, scheduledDate: v})}
                  className="w-full"
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditAcompteModal(false)}>
              Annuler
            </Button>
            <Button onClick={saveAcompteModification} className="bg-amber-600 hover:bg-amber-700">
              <Save className="h-4 w-4 mr-2" />
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
